import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import { usePortfolioBaseCCY, usePortfolioMarketValue } from '@/contexts/RADashboardDataContext/hooks'
import { RADashboardTheme } from '@/interfaces/raDashboard.interface'
import { formatMarketValue } from '@/services/report-dashboard/raDashboard.main.service'
import {
  CUSTOM_RANGE_KEY,
  DATE_RANGE_PRESETS,
  getPreviousBusinessDay,
  snapToWeekday,
  splitReportCoverage,
} from '@/utils/dateUtils'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import SettingsIcon from '@mui/icons-material/Settings'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'
import AttributionModelConfig from './AttributionModelConfig'

const ANALYZED_COLOR = '#22c55e'
const DISABLED_DAY_COLOR = '#bdbdbd'

// StaticDatePicker's internal sizing (day cells, header, reserved week-row space,
// etc.) is made up of several independent fixed dimensions, so shrinking it by
// overriding each one via sx is a losing battle. `zoom` scales the whole rendered
// box — and everything it reserves space for — uniformly and correctly, as long as
// its wrapper isn't being force-stretched by a flex sibling (see the `flex: '0 0
// auto'` wrappers below).
const CALENDAR_SCALE = 0.8

const YearJumpCalendarHeader = (props: PickersCalendarHeaderProps<Dayjs>) => {
  const { currentMonth, onMonthChange, onViewChange, view, views, minDate, maxDate } = props
  const prevMonth = currentMonth.subtract(1, 'month')
  const nextMonth = currentMonth.add(1, 'month')
  const prevYear = currentMonth.subtract(1, 'year')
  const nextYear = currentMonth.add(1, 'year')

  const prevMonthDisabled = !!minDate && prevMonth.endOf('month').isBefore(minDate.startOf('month'))
  const nextMonthDisabled = !!maxDate && nextMonth.startOf('month').isAfter(maxDate.endOf('month'))
  const prevYearDisabled = !!minDate && prevYear.endOf('month').isBefore(minDate.startOf('month'))
  const nextYearDisabled = !!maxDate && nextYear.startOf('month').isAfter(maxDate.endOf('month'))

  const handleLabelClick = () => {
    if (!onViewChange) return
    if (view === 'day' && views.includes('year')) onViewChange('year')
    else if (view === 'year' && views.includes('day')) onViewChange('day')
    else if (view === 'month' && views.includes('day')) onViewChange('day')
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}>
      <IconButton
        onClick={() => onMonthChange(prevYear, 'right')}
        disabled={prevYearDisabled}
        size='small'
        aria-label='Previous year'
      >
        <KeyboardDoubleArrowLeftIcon fontSize='small' />
      </IconButton>
      <IconButton
        onClick={() => onMonthChange(prevMonth, 'right')}
        disabled={prevMonthDisabled}
        size='small'
        aria-label='Previous month'
      >
        <ChevronLeftIcon fontSize='small' />
      </IconButton>
      <div
        onClick={handleLabelClick}
        style={{
          flex: 1,
          textAlign: 'center',
          cursor: 'pointer',
          fontWeight: 500,
          fontSize: '0.95rem',
          userSelect: 'none',
        }}
      >
        {currentMonth.format('MMMM YYYY')}
      </div>
      <IconButton
        onClick={() => onMonthChange(nextMonth, 'left')}
        disabled={nextMonthDisabled}
        size='small'
        aria-label='Next month'
      >
        <ChevronRightIcon fontSize='small' />
      </IconButton>
      <IconButton
        onClick={() => onMonthChange(nextYear, 'left')}
        disabled={nextYearDisabled}
        size='small'
        aria-label='Next year'
      >
        <KeyboardDoubleArrowRightIcon fontSize='small' />
      </IconButton>
    </div>
  )
}

const formatSelectedDateLabel = (isAttributionDashboard: boolean, displayDate: string, endDate?: string): string => {
  if (!displayDate) return ''
  if (isAttributionDashboard && endDate) {
    return ` for ${dayjs(displayDate).format('MM/DD/YYYY')} - ${dayjs(endDate).format('MM/DD/YYYY')}`
  }
  return ` for ${dayjs(displayDate).format('MM/DD/YYYY')}`
}

interface PortfolioConfigHeaderProps {
  theme: RADashboardTheme
  dashboardDisplayName: string
  onOpenAdvancedSettings: () => void
  portfolios: { name: string }[]
  selectedPortfolio: string
  onPortfolioChange: (portfolioName: string) => void
  benchmarks: { apiName: string; description: string }[]
  relatedBenchmarkName: string | null
  selectedBenchmark: string
  selectedBenchmarkName: string
  onBenchmarkChange: (apiName: string) => void
  dashboardType: FADashboardType
  displayDate: string
  onDateChange: (date: string) => void
  // Attribution dashboards select a date range instead of a single date.
  endDate?: string
  onEndDateChange?: (date: string) => void
  loadingDates: boolean
  availableDates: string[]
  reportLibraryDateSet: Set<string>
  engineBusy: boolean
  busyLabel: string
  onRunAnalysis: () => void
  collapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
  loadingMarketValue: boolean
}

const PortfolioConfigHeader: React.FC<PortfolioConfigHeaderProps> = ({
  theme,
  dashboardDisplayName,
  onOpenAdvancedSettings,
  portfolios,
  selectedPortfolio,
  onPortfolioChange,
  benchmarks,
  relatedBenchmarkName,
  selectedBenchmark,
  selectedBenchmarkName,
  onBenchmarkChange,
  dashboardType,
  displayDate,
  onDateChange,
  endDate,
  onEndDateChange,
  loadingDates,
  availableDates,
  reportLibraryDateSet,
  engineBusy,
  busyLabel,
  onRunAnalysis,
  collapsed,
  onCollapsedChange,
  loadingMarketValue,
}) => {
  const baseCurrency = usePortfolioBaseCCY()
  const marketValueNumber = usePortfolioMarketValue()
  const marketValue = marketValueNumber != null ? formatMarketValue(marketValueNumber) : null
  const [isPortfolioDropdownOpen, setIsPortfolioDropdownOpen] = useState<boolean>(false)
  const [isBenchmarkDropdownOpen, setIsBenchmarkDropdownOpen] = useState<boolean>(false)
  const [portfolioSearch, setPortfolioSearch] = useState<string>('')
  const [benchmarkSearch, setBenchmarkSearch] = useState<string>('')
  const [dateRangePreset, setDateRangePreset] = useState<string>(CUSTOM_RANGE_KEY)
  const [isDateRangeDropdownOpen, setIsDateRangeDropdownOpen] = useState<boolean>(false)
  const portfolioDropdownRef = useRef<HTMLDivElement>(null)
  const benchmarkDropdownRef = useRef<HTMLDivElement>(null)
  const dateRangeDropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (benchmarkDropdownRef.current && !benchmarkDropdownRef.current.contains(event.target as Node)) {
        setIsBenchmarkDropdownOpen(false)
        setBenchmarkSearch('')
      }
      if (portfolioDropdownRef.current && !portfolioDropdownRef.current.contains(event.target as Node)) {
        setIsPortfolioDropdownOpen(false)
        setPortfolioSearch('')
      }
      if (dateRangeDropdownRef.current && !dateRangeDropdownRef.current.contains(event.target as Node)) {
        setIsDateRangeDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const isAttributionDashboard = dashboardType === FADashboardType.Attribution

  const minAvailableDate = availableDates.length > 0 ? dayjs(availableDates[0].split('T')[0]) : undefined
  const maxAvailableDate =
    availableDates.length > 0 ? dayjs(availableDates[availableDates.length - 1].split('T')[0]) : dayjs()

  // End date every preset resolves against: the previous business day, clamped to the latest
  // date the portfolio actually has data for (e.g. the day after a market holiday is a business
  // day but may not have data yet).
  const previousBusinessDay = dayjs(getPreviousBusinessDay())
  const presetEndDate = previousBusinessDay.isAfter(maxAvailableDate, 'day') ? maxAvailableDate : previousBusinessDay

  // Dates the currently-applied preset actually produced, so an external change to
  // displayDate/endDate (e.g. a portfolio switch reconciling the range on its own) can be
  // told apart from the preset's own write and fall back to Custom instead of leaving a
  // stale preset label locking the (now mismatched) calendars.
  const appliedPresetDatesRef = useRef<{ begin: string; end: string } | null>(null)

  const handleDateRangePresetChange = (key: string) => {
    setDateRangePreset(key)
    setIsDateRangeDropdownOpen(false)
    if (key === CUSTOM_RANGE_KEY) {
      appliedPresetDatesRef.current = null
      return
    }
    const preset = DATE_RANGE_PRESETS.find((p) => p.key === key)
    if (!preset) return
    const begin = snapToWeekday(preset.getBeginDate(presetEndDate))
    const beginStr = begin.format('YYYY-MM-DD')
    const endStr = presetEndDate.format('YYYY-MM-DD')
    appliedPresetDatesRef.current = { begin: beginStr, end: endStr }
    onDateChange(beginStr)
    onEndDateChange?.(endStr)
  }

  // Presets whose begin date would fall before the portfolio's earliest available data are
  // hidden entirely (e.g. a portfolio loaded only this year shouldn't offer a 1 Year option),
  // and presets whose full span already has a report for every business day are highlighted
  // the same way the calendar marks already-generated dates.
  const visibleDateRangePresets = DATE_RANGE_PRESETS.map((preset) => {
    const begin = snapToWeekday(preset.getBeginDate(presetEndDate))
    return { preset, begin }
  })
    .filter(({ begin }) => !minAvailableDate || !begin.isBefore(minAvailableDate, 'day'))
    .map(({ preset, begin }) => {
      const { coverableDates, missingDates } = splitReportCoverage(
        begin.format('YYYY-MM-DD'),
        presetEndDate.format('YYYY-MM-DD'),
        availableDates,
        reportLibraryDateSet,
      )
      const alreadyGenerated = coverableDates.length > 0 && missingDates.length === 0
      return { key: preset.key, label: preset.label, alreadyGenerated }
    })

  useEffect(() => {
    if (dateRangePreset === CUSTOM_RANGE_KEY) return
    const applied = appliedPresetDatesRef.current
    if (!applied || applied.begin !== displayDate || applied.end !== endDate) {
      setDateRangePreset(CUSTOM_RANGE_KEY)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayDate, endDate])

  const dateRangePresetLabel =
    dateRangePreset === CUSTOM_RANGE_KEY
      ? 'Custom'
      : DATE_RANGE_PRESETS.find((p) => p.key === dateRangePreset)?.label ?? 'Custom'

  const renderCalendar = (
    value: string,
    onChange: (date: string) => void,
    calendarMinDate?: Dayjs,
    calendarMaxDate?: Dayjs,
    disabled?: boolean,
  ) => (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StaticDatePicker
        // While dates load the valid range isn't known yet, so show the calendar
        // empty rather than briefly flagging the preset date invalid (red).
        value={loadingDates ? null : value ? dayjs(value) : null}
        onChange={(newValue: Dayjs | null) => {
          if (newValue && newValue.isValid()) {
            onChange(newValue.format('YYYY-MM-DD'))
          } else {
            onChange('')
          }
        }}
        disabled={disabled}
        displayStaticWrapperAs='desktop'
        views={['year', 'month', 'day']}
        openTo='day'
        slots={{ calendarHeader: YearJumpCalendarHeader, toolbar: () => null, actionBar: () => null }}
        minDate={calendarMinDate ?? minAvailableDate}
        maxDate={calendarMaxDate ?? maxAvailableDate}
        shouldDisableDate={(date: Dayjs) => {
          const day = date.day()
          if (day === 0 || day === 6) return true
          // calendarMinDate/calendarMaxDate, when passed explicitly (begin/end pickers bounding
          // each other), is the other endpoint's date — the backend rejects a beginDate/endDate
          // pair that's exactly equal, so that day can't be picked for this endpoint either.
          if (calendarMinDate && date.isSame(calendarMinDate, 'day')) return true
          if (calendarMaxDate && date.isSame(calendarMaxDate, 'day')) return true
          return false
        }}
        slotProps={{
          day: (ownerState: { day: Dayjs }) => {
            const reportExists = reportLibraryDateSet.has(ownerState.day.format('YYYY-MM-DD'))
            return reportExists
              ? {
                  sx: {
                    fontWeight: 700,
                    color: ANALYZED_COLOR,
                  },
                }
              : {}
          },
        }}
        sx={{
          '& .MuiPickersLayout-contentWrapper': {
            color: theme.textPrimary,
          },
        }}
      />
    </LocalizationProvider>
  )

  const headingStyle: React.CSSProperties = {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: theme.textPrimary,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  }

  const chevronStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: '20px',
    color: theme.textPrimary,
    cursor: 'pointer',
    display: 'inline-block',
    lineHeight: 1,
  }

  return (
    <div
      style={{
        margin: '0 40px',
        marginBottom: '30px',
        position: 'relative',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          backgroundColor: theme.subheaderBackground,
          borderRadius: 4,
          padding: '16px 24px',
        }}
      >
        <div onClick={() => onCollapsedChange(false)} style={{ cursor: 'pointer' }}>
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: theme.textPrimary,
                }}
              >
                {dashboardDisplayName} for:
              </span>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: theme.textPrimary,
                  fontSize: '15px',
                  fontWeight: 600,
                }}
              >
                <span>
                  {selectedPortfolio || 'Select Portfolio'}
                  {formatSelectedDateLabel(isAttributionDashboard, displayDate, endDate)}
                </span>
                <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
                  <path
                    d='M2 4L6 8L10 4'
                    stroke={theme.textPrimary}
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
            <div
              style={{
                marginTop: '8px',
                fontSize: '16px',
                color: theme.textSecondary,
              }}
            >
              Benchmark:
              <span style={{ color: theme.textPrimary, marginLeft: '4px', fontWeight: 600 }}>
                {selectedBenchmarkName || 'None'}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            fontSize: '18px',
            fontWeight: 500,
            color: theme.textSecondary,
            whiteSpace: 'nowrap',
            alignSelf: 'center',
          }}
        >
          Total Portfolio Value:{' '}
          <span style={{ fontWeight: 700, color: theme.textPrimary }}>
            {!loadingMarketValue && marketValue && (
              <>
                {marketValue}
                <span style={{ marginLeft: '4px', fontWeight: 600 }}>{baseCurrency}</span>
              </>
            )}
          </span>
        </div>
      </div>

      {!collapsed && (
        <div
          style={{
            position: 'absolute',
            top: '40px',
            left: 0,
            minWidth: '500px',
            maxHeight: 'calc(100vh - 150px)',
            zIndex: 20,
            backgroundColor: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            boxShadow: `0 8px 24px ${theme.boxShadowColor}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '24px',
              }}
            >
              <h2 onClick={() => onCollapsedChange(true)} style={headingStyle}>
                Portfolio Configuration
              </h2>
              <span onClick={() => onCollapsedChange(true)} style={{ ...chevronStyle, transform: 'rotate(-90deg)' }}>
                {'❭'}
              </span>
            </div>

            {/* Configuration Fields (left) + Attribution Model (right, attribution only) */}
            <div style={{ display: 'flex', gap: '24px', flex: 1, minHeight: 0 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '12px',
                    fontWeight: '600',
                    color: theme.textPrimary,
                    fontSize: '14px',
                  }}
                >
                  Portfolio
                </label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{ position: 'relative', flex: 1 }} ref={portfolioDropdownRef}>
                    <div
                      onClick={() => setIsPortfolioDropdownOpen(!isPortfolioDropdownOpen)}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        border: `1px solid ${theme.border}`,
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        backgroundColor: theme.cardBackground,
                        color: theme.textPrimary,
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxShadow: `0 2px 8px ${theme.boxShadowColor}`,
                      }}
                    >
                      <span>{selectedPortfolio || 'Select Portfolio'}</span>
                      <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
                        <path
                          d='M2 4L6 8L10 4'
                          stroke={theme.textPrimary}
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </div>
                    {isPortfolioDropdownOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          width: '100%',
                          backgroundColor: theme.cardBackground,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '8px',
                          marginTop: '4px',
                          zIndex: 1000,
                          boxShadow: `0 4px 12px ${theme.benchmarkBoxShadowColor}`,
                        }}
                      >
                        <div style={{ padding: '8px' }}>
                          <input
                            autoFocus
                            placeholder='Search portfolio...'
                            value={portfolioSearch}
                            onChange={(e) => setPortfolioSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: `1px solid ${theme.border}`,
                              borderRadius: '8px',
                              fontSize: '14px',
                              backgroundColor: theme.inputBackground,
                              color: theme.textPrimary,
                              boxSizing: 'border-box',
                              outline: 'none',
                            }}
                          />
                        </div>
                        <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                          {portfolios
                            .filter(
                              (p: { name: string }) =>
                                p.name !== 'Select Portfolio' &&
                                p.name.toLowerCase().includes(portfolioSearch.toLowerCase()),
                            )
                            .map((portfolio: { name: string }) => (
                              <div
                                key={portfolio.name}
                                onClick={() => {
                                  onPortfolioChange(portfolio.name)
                                  setIsPortfolioDropdownOpen(false)
                                  setPortfolioSearch('')
                                }}
                                style={{
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  backgroundColor:
                                    selectedPortfolio === portfolio.name ? theme.inputBackground : 'transparent',
                                  color: theme.textPrimary,
                                }}
                              >
                                <span>{portfolio.name}</span>
                                {selectedPortfolio === portfolio.name && (
                                  <span
                                    style={{
                                      color: '#059669',
                                      fontSize: '14px',
                                    }}
                                  >
                                    ✓
                                  </span>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <IconButton
                    onClick={onOpenAdvancedSettings}
                    title='Advanced Settings'
                    aria-label='Advanced Settings'
                    sx={{
                      color: theme.textPrimary,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      padding: '11px',
                    }}
                  >
                    <SettingsIcon />
                  </IconButton>
                </div>
              </div>

              <div style={{ position: 'relative' }} ref={benchmarkDropdownRef}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '12px',
                    fontWeight: '600',
                    color: theme.textPrimary,
                    fontSize: '14px',
                  }}
                >
                  Benchmark
                </label>
                <div
                  onClick={() => setIsBenchmarkDropdownOpen(!isBenchmarkDropdownOpen)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    backgroundColor: theme.cardBackground,
                    color: theme.textPrimary,
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: `0 2px 8px ${theme.boxShadowColor}`,
                  }}
                >
                  <span>{selectedBenchmarkName || 'Select benchmark'}</span>
                  <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
                    <path
                      d='M2 4L6 8L10 4'
                      stroke={theme.textPrimary}
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>

                {isBenchmarkDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      width: '100%',
                      backgroundColor: theme.cardBackground,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      marginTop: '4px',
                      zIndex: 1000,
                      boxShadow: `0 4px 12px ${theme.benchmarkBoxShadowColor}`,
                    }}
                  >
                    <div style={{ padding: '8px' }}>
                      <input
                        autoFocus
                        placeholder='Search benchmark...'
                        value={benchmarkSearch}
                        onChange={(e) => setBenchmarkSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: `1px solid ${theme.border}`,
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: theme.inputBackground,
                          color: theme.textPrimary,
                          boxSizing: 'border-box',
                          outline: 'none',
                        }}
                      />
                    </div>
                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                      {benchmarks
                        .filter((b: { apiName: string; description: string }) =>
                          b.description.toLowerCase().includes(benchmarkSearch.toLowerCase()),
                        )
                        .map(
                          (
                            benchmark: {
                              apiName: string
                              description: string
                            },
                            index: number,
                          ) => {
                            const isRelated =
                              index === 0 &&
                              relatedBenchmarkName &&
                              (benchmark.description === relatedBenchmarkName ||
                                benchmark.apiName === relatedBenchmarkName)
                            const isSelected = benchmark.apiName === selectedBenchmark

                            return (
                              <div
                                key={benchmark.apiName}
                                onClick={() => {
                                  onBenchmarkChange(benchmark.apiName)
                                  setIsBenchmarkDropdownOpen(false)
                                  setBenchmarkSearch('')
                                }}
                                style={{
                                  padding: '12px 16px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? theme.inputBackground : 'transparent',
                                  fontWeight: isRelated ? '800' : '400',
                                  color: isRelated ? '#059669' : theme.textPrimary,
                                }}
                              >
                                <span>{benchmark.description}</span>
                                {isSelected && (
                                  <span
                                    style={{
                                      color: '#059669',
                                      fontSize: '14px',
                                    }}
                                  >
                                    ✓
                                  </span>
                                )}
                              </div>
                            )
                          },
                        )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '12px',
                  }}
                >
                  <label
                    style={{
                      fontWeight: '600',
                      color: theme.textPrimary,
                      fontSize: '14px',
                    }}
                  >
                    {isAttributionDashboard ? 'Date Range' : 'Analysis Date'}
                  </label>
                  <Tooltip
                    arrow
                    title={
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '2px' }}>
                        <div style={{ fontWeight: 600 }}>Calendar Legend</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: '10px',
                              height: '10px',
                              borderRadius: '2px',
                              backgroundColor: ANALYZED_COLOR,
                            }}
                          />
                          <span>Report already run for this date</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: '10px',
                              height: '10px',
                              borderRadius: '2px',
                              border: `1px solid ${DISABLED_DAY_COLOR}`,
                              backgroundColor: 'transparent',
                            }}
                          />
                          <span>Selectable date — report may be generated</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: '10px',
                              height: '10px',
                              borderRadius: '2px',
                              backgroundColor: DISABLED_DAY_COLOR,
                            }}
                          />
                          <span>Not selectable — weekend or outside the available date range</span>
                        </div>
                      </div>
                    }
                    slotProps={{
                      tooltip: { sx: { fontSize: '13px', padding: '10px 12px', maxWidth: 280 } },
                    }}
                  >
                    <InfoOutlinedIcon
                      fontSize='small'
                      style={{ color: theme.textSecondary, cursor: 'help' }}
                      aria-label='Calendar legend'
                    />
                  </Tooltip>
                </div>
                {isAttributionDashboard ? (
                  <>
                    <div style={{ position: 'relative', marginBottom: '12px' }} ref={dateRangeDropdownRef}>
                      <div
                        onClick={() => setIsDateRangeDropdownOpen(!isDateRangeDropdownOpen)}
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          border: `1px solid ${theme.border}`,
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          backgroundColor: theme.cardBackground,
                          color: theme.textPrimary,
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          boxShadow: `0 2px 8px ${theme.boxShadowColor}`,
                        }}
                      >
                        <span>{dateRangePresetLabel}</span>
                        <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
                          <path
                            d='M2 4L6 8L10 4'
                            stroke={theme.textPrimary}
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </div>

                      {isDateRangeDropdownOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            width: '100%',
                            backgroundColor: theme.cardBackground,
                            border: `1px solid ${theme.border}`,
                            borderRadius: '8px',
                            marginTop: '4px',
                            zIndex: 1000,
                            boxShadow: `0 4px 12px ${theme.benchmarkBoxShadowColor}`,
                          }}
                        >
                          <div style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                            {[
                              { key: CUSTOM_RANGE_KEY, label: 'Custom', alreadyGenerated: false },
                              ...visibleDateRangePresets,
                            ].map((preset) => {
                              const isSelected = preset.key === dateRangePreset
                              return (
                                <div
                                  key={preset.key}
                                  onClick={() => handleDateRangePresetChange(preset.key)}
                                  style={{
                                    padding: '12px 16px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? theme.inputBackground : 'transparent',
                                    color: theme.textPrimary,
                                  }}
                                >
                                  <span
                                    style={
                                      preset.alreadyGenerated ? { color: ANALYZED_COLOR, fontWeight: 500 } : undefined
                                    }
                                  >
                                    {preset.label}
                                  </span>
                                  {isSelected && (
                                    <span
                                      style={{
                                        color: '#059669',
                                        fontSize: '14px',
                                      }}
                                    >
                                      ✓
                                    </span>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                      <div style={{ flex: '0 0 auto' }}>
                        <div
                          style={{
                            marginBottom: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: theme.textSecondary,
                          }}
                        >
                          Begin Date
                        </div>
                        <div
                          style={{
                            width: 'fit-content',
                            borderRadius: '8px',
                            backgroundColor: theme.cardBackground,
                            cursor: loadingDates ? 'wait' : 'default',
                            zoom: CALENDAR_SCALE,
                          }}
                        >
                          {renderCalendar(
                            displayDate,
                            onDateChange,
                            undefined,
                            endDate ? dayjs(endDate) : undefined,
                            dateRangePreset !== CUSTOM_RANGE_KEY,
                          )}
                        </div>
                      </div>
                      <div style={{ flex: '0 0 auto' }}>
                        <div
                          style={{
                            marginBottom: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: theme.textSecondary,
                          }}
                        >
                          End Date
                        </div>
                        <div
                          style={{
                            width: 'fit-content',
                            borderRadius: '8px',
                            backgroundColor: theme.cardBackground,
                            cursor: loadingDates ? 'wait' : 'default',
                            zoom: CALENDAR_SCALE,
                          }}
                        >
                          {renderCalendar(
                            endDate ?? '',
                            onEndDateChange ?? (() => {}),
                            displayDate ? dayjs(displayDate) : undefined,
                            undefined,
                            dateRangePreset !== CUSTOM_RANGE_KEY,
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      width: 'fit-content',
                      margin: '0 auto',
                      borderRadius: '8px',
                      backgroundColor: theme.cardBackground,
                      cursor: loadingDates ? 'wait' : 'default',
                      zoom: CALENDAR_SCALE,
                    }}
                  >
                    {renderCalendar(displayDate, onDateChange)}
                  </div>
                )}
              </div>
            </div>
            {isAttributionDashboard && (
              <div style={{ flex: '0 0 auto', overflowY: 'auto' }}>
                <AttributionModelConfig theme={theme} />
              </div>
            )}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingTop: '24px',
                borderTop: `1px solid ${theme.border}`,
                gap: '12px',
              }}
            >
              <button
                style={{
                  backgroundColor: 'transparent',
                  color: theme.textPrimary,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
                onClick={() => onCollapsedChange(true)}
                title='Cancel'
              >
                Cancel
              </button>
              <button
                style={{
                  backgroundColor: theme.runButtonBackground,
                  color: theme.runButtonColor,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: engineBusy ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  boxShadow: `0 4px 12px ${theme.runButtonBoxShadowColor}`,
                  opacity: engineBusy ? 0.7 : 1,
                }}
                onClick={engineBusy ? undefined : onRunAnalysis}
                title={engineBusy ? 'Generating Report...' : 'Run Analysis'}
                disabled={engineBusy}
              >
                {engineBusy ? (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      style={{ animation: 'spin 1s linear infinite' }}
                    >
                      <path d='M21 12a9 9 0 11-6.219-8.56' />
                    </svg>
                    {busyLabel}
                  </>
                ) : (
                  <>
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                      <polygon points='5 3 19 12 5 21 5 3' />
                    </svg>
                    Run Analysis
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PortfolioConfigHeader
